import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { switchMap } from 'rxjs/operators';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { TenantContextService } from '@app/core/services/tenant-context/tenant-context.service';
import { AdminTenantService } from '@app/features/admin/tenants/services/admin-tenant.service';
import { AuthService } from '@app/core/services/auth/auth.service';
import { AdminTenantSummary } from '@app/core/models/admin-tenant.interface';
import { HOME_ROUTE } from '@app/core/constants/routes.constants';

@Component({
  selector: 'app-tenant-badge',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    ScrollPanelModule,
  ],
  templateUrl: './tenant-badge.component.html',
  styleUrls: ['./tenant-badge.component.scss'],
})
export class TenantBadgeComponent implements OnInit {
  tenantContextService = inject(TenantContextService);
  private adminTenantService = inject(AdminTenantService);
  private authService = inject(AuthService);
  private router = inject(Router);

  role: string | null = null;
  showSwitcher = false;
  tenants: AdminTenantSummary[] = [];
  filteredTenants: AdminTenantSummary[] = [];
  searchQuery = '';
  loading = false;

  async ngOnInit(): Promise<void> {
    this.role = await this.authService.getUserRole();
    if (this.role === 'system_admin') {
      this.loadTenants();
    } else if (
      this.role === 'tenant_admin' &&
      this.tenantContextService.selectedTenantName() === null
    ) {
      // Only tenant admins may read the tenant record; for a workshop manager the
      // call returns 403 and the interceptor redirects to the unauthorized page.
      this.loadCurrentTenantName();
    }
  }

  private loadCurrentTenantName(): void {
    fromPromise(this.authService.getTenantId())
      .pipe(switchMap((tenantId) => this.adminTenantService.getTenant(tenantId)))
      .subscribe({
        next: (tenant) => {
          this.tenantContextService.switchTenant(tenant.tenantId, tenant.name);
        },
        error: () => {
          // silently ignore — badge will show the fallback 'Tenant' label
        },
      });
  }

  private loadTenants(): void {
    this.loading = true;
    this.adminTenantService.listTenants({ page: 1, limit: 100 }).subscribe({
      next: (response) => {
        this.tenants = response.data;
        this.filteredTenants = [...this.tenants];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  openSwitcher(): void {
    this.searchQuery = '';
    this.filteredTenants = [...this.tenants];
    this.showSwitcher = true;
  }

  onSearch(): void {
    const q = this.searchQuery.toLowerCase().trim();
    if (!q) {
      this.filteredTenants = [...this.tenants];
    } else {
      this.filteredTenants = this.tenants.filter(
        (t) =>
          t.name.toLowerCase().includes(q) || (t.tradingAs && t.tradingAs.toLowerCase().includes(q))
      );
    }
  }

  selectTenant(tenant: AdminTenantSummary): void {
    this.tenantContextService.switchTenant(tenant.tenantId, tenant.name);
    this.showSwitcher = false;
    this.router.navigate([HOME_ROUTE]);
  }

  get displayName(): string {
    return this.tenantContextService.selectedTenantName() ?? 'Tenant';
  }
}
